import { __assign, __read, __rest, __values } from "tslib";
import React, { useState, useRef, useEffect, Fragment } from "react";
import { Dropdown } from "../dropdown";
import { Checkbox } from "../checkbox";
import { useDefaultValue } from "../form";
import { CheckTree } from "../checktree";
import { Button } from "../button";
import { Text } from "../text";
import { useTranslation } from "../i18n";
import { useConfig } from "../_util/config-context";
import { EmptyTip } from "../tips";
import { SearchBox } from "../searchbox";
import { injectValue } from "../_util/inject-value";
import { Tooltip } from "../tooltip";
import { useDefault } from "../_util/use-default";
import { noop } from "../_util/noop";
import { searchFilter } from "../_util/search-filter";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { isMobile } from "../_util/is-mobile";
import { useUnmounted } from "../_util/use-unmounted";
import { getListItems } from "./util";
import { VirtualizedList } from "../list/VirtualizedList";
export var SelectMultiple = forwardRefWithStatics(function SelectMultiple(props, ref) {
    var e_1, _a;
    var t = useTranslation();
    var classPrefix = useConfig().classPrefix;
    var _b = useDefaultValue(props, []), _c = _b.staging, staging = _c === void 0 ? true : _c, value = _b.value, onChange = _b.onChange, _d = _b.options, options = _d === void 0 ? [] : _d, groups = _b.groups, allOption = _b.allOption, _e = _b.shouldOptionExcludeFromAll, shouldOptionExcludeFromAll = _e === void 0 ? "disabled" : _e, _f = _b.allowEmpty, allowEmpty = _f === void 0 ? true : _f, _g = _b.onOpen, onOpen = _g === void 0 ? noop : _g, _h = _b.onClose, onClose = _h === void 0 ? noop : _h, searchable = _b.searchable, _j = _b.searchPlaceholder, searchPlaceholder = _j === void 0 ? "" : _j, _k = _b.onSearch, onSearch = _k === void 0 ? noop : _k, customizeFilter = _b.filter, filterRender = _b.filterRender, _l = _b.autoClearSearchValue, autoClearSearchValue = _l === void 0 ? true : _l, _m = _b.defaultSearchValue, defaultSearchValue = _m === void 0 ? "" : _m, searchValue = _b.searchValue, onSearchValueChange = _b.onSearchValueChange, clearable = _b.clearable, _o = _b.onScrollBottom, onScrollBottom = _o === void 0 ? noop : _o, bottomTips = _b.bottomTips, footer = _b.footer, _p = _b.virtual, virtual = _p === void 0 ? false : _p, listWidth = _b.listWidth, listHeight = _b.listHeight, _q = _b.boxStyle, boxStyle = _q === void 0 ? {} : _q, dropdownProps = __rest(_b, ["staging", "value", "onChange", "options", "groups", "allOption", "shouldOptionExcludeFromAll", "allowEmpty", "onOpen", "onClose", "searchable", "searchPlaceholder", "onSearch", "filter", "filterRender", "autoClearSearchValue", "defaultSearchValue", "searchValue", "onSearchValueChange", "clearable", "onScrollBottom", "bottomTips", "footer", "virtual", "listWidth", "listHeight", "boxStyle"]);
    // 移动端 focus 可能产生滚动导致关闭
    if (isMobile && searchable) {
        dropdownProps.closeOnScroll = false;
    }
    var vListRef = useRef(null);
    var _r = __read(useState(value), 2), stagingValue = _r[0], setStagingValue = _r[1];
    var InputRef = useRef(null);
    var _s = __read(useDefault(searchValue, defaultSearchValue, onSearchValueChange), 2), inputValue = _s[0], setInputValue = _s[1];
    var unmountedRef = useUnmounted();
    var filter = customizeFilter ||
        (function (inputValue, _a) {
            var text = _a.text, value = _a.value;
            var optionText = String(typeof text === "string" ? text : value);
            return !searchable || searchFilter(optionText, inputValue);
        });
    var hasGroup = !!options.find(function (opt) { return !!opt.groupKey; });
    function focus() {
        if (!isMobile && searchable) {
            setTimeout(function () {
                if (InputRef.current) {
                    InputRef.current.focus();
                }
            }, 100); // 第一次展开时 Input 还未渲染
        }
    }
    var handleChange = function (value, context) {
        if (staging === false) {
            onChange(value, context);
            return;
        }
        setStagingValue(value);
    };
    // 「全部」选项和其余选项是数关系
    var relations = {};
    if (allOption) {
        try {
            for (var options_1 = __values(options), options_1_1 = options_1.next(); !options_1_1.done; options_1_1 = options_1.next()) {
                var option = options_1_1.value;
                if (
                // 用户可以指定哪些选项从全选逻辑中排除
                (typeof shouldOptionExcludeFromAll === "function" &&
                    shouldOptionExcludeFromAll(option)) ||
                    (shouldOptionExcludeFromAll === "disabled" && option.disabled)) {
                    // continue
                }
                else {
                    relations[option.value] = allOption.value;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (options_1_1 && !options_1_1.done && (_a = options_1.return)) _a.call(options_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    // 筛选
    var filteredOptions = options.filter(function (options) {
        return filter(inputValue, options);
    });
    var tips = props.tips;
    if (!tips && filteredOptions.length === 0) {
        tips = React.createElement(EmptyTip, null);
    }
    var items = getListItems({
        tips: tips ? injectValue(tips)(filteredOptions) : null,
        bottomTips: injectValue(bottomTips)(filteredOptions),
        groups: groups,
        options: filteredOptions,
        allOption: allOption,
    }).map(function (item) {
        if (item.type === "option" && searchable && filterRender) {
            return __assign(__assign({}, item), { text: filterRender(inputValue, item.option) });
        }
        return item;
    });
    if (listWidth) {
        boxStyle.width = listWidth;
        boxStyle.minWidth = listWidth;
    }
    // 重置数据
    var resetData = function () {
        if (inputValue !== "") {
            setInputValue("", {});
        }
        handleChange([], {});
    };
    return (React.createElement(Dropdown, __assign({ ref: ref }, dropdownProps, { preventMouseDown: !searchable && !footer, updateOnChildrenChange: true, button: React.createElement(ValueBrief, __assign({}, props, { value: value })), onOpen: function () {
            setStagingValue(value);
            focus();
            onOpen();
        }, onClose: function () {
            if (autoClearSearchValue && inputValue !== "") {
                setTimeout(function () {
                    if (!unmountedRef.current) {
                        setInputValue("", {});
                    }
                }, 100);
            }
            onClose();
        }, clickClose: false, clearable: value.length && clearable, boxStyle: boxStyle, onClear: function (event) {
            onChange([], { event: event });
            setStagingValue([]);
        } }), function (close) { return (React.createElement(CheckTree, { relations: relations, value: (staging ? stagingValue : value) || value, onChange: handleChange, disabledNames: options.filter(function (x) { return x.disabled; }).map(function (x) { return x.value; }) },
        searchable && (React.createElement(SearchBox, { simple: true, ref: InputRef, value: inputValue, onChange: function (value, context) {
                setInputValue(value, context);
                onSearch(value, context);
            }, onClear: focus, placeholder: searchPlaceholder, onSearch: onSearch })),
        React.createElement(VirtualizedList, { virtual: virtual, containerStyle: boxStyle, className: classPrefix + "-list--checkoption", containerClassName: dropdownProps.boxClassName, items: items.map(function (item) {
                if (item.type === "option") {
                    var option = item.option;
                    var content = option.text || option.value;
                    return __assign(__assign({}, item), { text: (React.createElement(Checkbox, { onClick: function (evt) { return evt.stopPropagation(); }, name: option.value, title: !option.tooltip && typeof content === "string"
                                ? content
                                : undefined }, content)), props: {
                            disabled: option.disabled,
                            tooltip: option.tooltip,
                        } });
                }
                return item;
            }), virtualizedRef: vListRef, type: hasGroup ? "option-group" : "option", onScrollBottom: onScrollBottom, listHeight: listHeight }),
        (footer || staging !== false) && (React.createElement(Dropdown.Footer, null,
            footer,
            staging !== false && (React.createElement(React.Fragment, null,
                React.createElement(Button, { type: "primary", htmlType: "button", disabled: !allowEmpty && stagingValue.length === 0, onClick: function (event) {
                        onChange(stagingValue, { event: event });
                        close();
                    } }, t.okText),
                React.createElement(Button, { type: "weak", htmlType: "button", onClick: resetData }, t.resetText))))))); }));
}, {
    defaultLabelAlign: "middle",
});
SelectMultiple.displayName = "SelectMultiple";
function ValueBrief(props) {
    var t = useTranslation();
    var value = props.value, allOption = props.allOption, options = props.options, button = props.button, _a = props.placeholder, placeholder = _a === void 0 ? t.pleaseSelect : _a, _b = props.appearance, appearance = _b === void 0 ? props.appearence : _b;
    var buttonPlaceholder = placeholder;
    if (!appearance || appearance === "button" || appearance === "default") {
        buttonPlaceholder = React.createElement(Text, { theme: "weak" }, placeholder);
    }
    var _c = __read(useState((value || []).map(function (x) { return options.find(function (y) { return y.value === x; }) || { value: x }; })), 2), selectedOptions = _c[0], setSelectedOptions = _c[1];
    useEffect(function () {
        setSelectedOptions(function (selectedOptions) {
            return (value || [])
                .map(function (v) {
                return options.find(function (option) { return option.value === v; }) ||
                    selectedOptions.find(function (option) { return option.value === v; });
            })
                .filter(Boolean);
        });
    }, [options, value]);
    if (button) {
        return React.createElement("div", null, injectValue(button)(selectedOptions));
    }
    if (!value || !value.length) {
        return React.createElement(React.Fragment, null, buttonPlaceholder);
    }
    if (allOption &&
        value.length === options.length &&
        value.filter(function (v) { return options.find(function (o) { return o.value === v; }); }).length ===
            options.length) {
        return React.createElement("span", null, allOption.text || allOption.value);
    }
    var exceed = selectedOptions.length > 5;
    return (React.createElement(Tooltip, { title: selectedOptions.map(function (o, index) { return (React.createElement(Fragment, { key: o.value },
            o.text || o.value,
            index < selectedOptions.length - 1 && ", ")); }) },
        React.createElement(Text, { overflow: true },
            selectedOptions.slice(0, 5).map(function (o, index) { return (React.createElement("span", { key: o.value },
                o.text || o.value,
                index < selectedOptions.length - 1 && ", ")); }),
            exceed && React.createElement("span", null, "..."))));
}
ValueBrief.displayName = "SelectValueBrief";
//# sourceMappingURL=SelectMultiple.js.map